import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    LuCalendar,
    LuChevronRight,
    LuCircleAlert,
    LuCircleHelp,
    LuListChecks,
    LuMessageSquare,
    LuPlay,
    LuTarget,
    LuTrendingUp,
} from 'react-icons/lu';
import aviso from '../lib/aviso';
import AssistenteComposer from '../components/AssistenteComposer';
import AssistenteMensagem from '../components/AssistenteMensagem';
import AssistentePensando from '../components/AssistentePensando';
import AvisoConfirmarEmail from '../components/AvisoConfirmarEmail';
import BrikApresentacao from '../components/BrikApresentacao';
import BrikFaixa from '../components/BrikFaixa';
import BrikNovidadeGanhas from '../components/BrikNovidadeGanhas';
import BrikFala, { type EstadoFala } from '../components/BrikFala';
import SeloMeta from '../components/SeloMeta';
import { usePrimeirosPassos } from '../contexts/PrimeirosPassosContext';
import { useConversaAssistente } from '../hooks/useConversaAssistente';
import { useCelular } from '../hooks/useCelular';
import AssistenteService, {
    CONVERSAS_ASSISTENTE_EVENT,
    type AberturaAssistente,
    type ConversaAssistente,
    type OrigemPergunta,
} from '../services/AssistenteService';
import { NotificacaoService } from '../services/NotificacaoService';
import { abrirNovidade, liberarHome, midiaNoAr, novidadesVigentes, videoDaNovidadeNoAr, type Novidade } from '../config/novidades';
import { quando } from '../utils/quando';
import './AssistenteLayout.css';

/*
 * A Home do Brik AI (07/10/2026). Desenho fechado:
 * docs/designsystem/propostas/home-brik.html — o Brik à esquerda falando num
 * balão (opção B), o campo, as sugestões e, embaixo, as conversas recentes. Sem
 * lista de itens e sem fechamento do dia, por decisão dele.
 *
 * Perguntar não muda de tela: a conversa acontece aqui mesmo, com o campo preso
 * embaixo como em /assistente. Quando ela começa, o balão se recolhe numa faixa
 * no topo, com o Brik (BrikFaixa; direção B de
 * docs/designsystem/propostas/home-brik-conversa.html, 07/10/2026). Sair da Home
 * e voltar abre sempre a tela inicial; a conversa fica nas recentes.
 */

/* As sugestões de antes da Home do Brik: voltam quando a abertura falha. */
const SUGESTOES_DE_SEMPRE = [
    'Como estamos na meta deste mês?',
    'O que parou de andar?',
    'Vamos bater a meta?',
];

/* O ícone de cada sugestão sai do assunto dela — a API manda só o texto. */
const iconeDaSugestao = (texto: string): ReactNode => {
    const t = texto.toLowerCase();
    if (t.includes('tarefa')) return <LuListChecks size={14} />;
    if (t.includes('agenda')) return <LuCalendar size={14} />;
    if (t.includes('bater a meta')) return <LuTrendingUp size={14} />;
    if (t.includes('meta')) return <LuTarget size={14} />;
    if (t.includes('parou')) return <LuCircleAlert size={14} />;
    if (t.startsWith('como ')) return <LuCircleHelp size={14} />;
    return <LuMessageSquare size={14} />;
};

/* O lugar das sugestões enquanto a fala carrega, nas larguras das três de sempre
   (medidas em 1440): a Home se centraliza na altura, e sem a reserva o conjunto
   subia quando elas chegavam (opção B, 07/10/2026). */
const RESERVA_DAS_SUGESTOES = [212, 306, 244];

/* Conta nova: a pergunta que ajuda no próximo passo (vai para a ajuda do Brik). */
const PERGUNTA_DO_PASSO: Record<string, string> = {
    canais: 'Como conecto o WhatsApp?',
    agente: 'Como crio o agente de IA?',
    produtos: 'Como cadastro meus produtos?',
    usuarios: 'Como convido a minha equipe?',
};

const saudacao = (hora: number) => {
    if (hora < 12) return 'Bom dia';
    if (hora < 18) return 'Boa tarde';
    return 'Boa noite';
};

const primeiroNome = (nome?: string) => (nome ?? '').trim().split(/\s+/)[0] || '';

/* Quantas conversas recentes a Home mostrou da última vez, por pessoa. Enquanto a
   lista carrega, a Home guarda esse espaço: o conjunto é centralizado na altura e,
   sem a reserva, subia quando a lista chegava. Sem armazenamento, não reserva. */
const CHAVE_HISTORICO = 'lemeia:home-historico:';
const lerTamanhoDoHistorico = (usuarioId: string) => {
    try { return Math.min(5, Math.max(0, Number(localStorage.getItem(CHAVE_HISTORICO + usuarioId)) || 0)); }
    catch { return 0; }
};
const guardarTamanhoDoHistorico = (usuarioId: string, n: number) => {
    try { localStorage.setItem(CHAVE_HISTORICO + usuarioId, String(n)); } catch { /* sem armazenamento */ }
};

const HomePage = () => {
    const celular = useCelular();
    const navigate = useNavigate();

    const usuario = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user') || 'null'); }
        catch { return null; }
    }, []);
    const nome = primeiroNome(usuario?.nome);
    const usuarioId = String(usuario?.id ?? usuario?.userId ?? 'anon');

    /* ─── A fala do Brik ─────────────────────────────────────── */
    const [abertura, setAbertura] = useState<AberturaAssistente | null>(null);
    const [estado, setEstado] = useState<EstadoFala>('carregando');
    const [apresentando, setApresentando] = useState(false);

    useEffect(() => {
        let vivo = true;
        AssistenteService.abertura()
            .then(a => {
                if (!vivo) return;
                setAbertura(a);
                setEstado('pronto');
                if (a.apresentacaoVista) { liberarHome(); return; }
                /* Com o vídeo da novidade no ar, ele é a apresentação (decisão de
                   08/10/2026: "só o vídeo"); a estática volta só sem ele. */
                videoDaNovidadeNoAr().then(noAr => {
                    if (!vivo) return;
                    if (noAr) liberarHome();
                    else setApresentando(true);
                });
            })
            /* Silencioso de propósito: sem a fala, a Home continua servindo para
               perguntar — o balão diz isso em uma linha. */
            .catch(() => { if (vivo) setEstado('erro'); liberarHome(); });
        return () => { vivo = false; };
    }, []);

    const comecar = () => {
        setApresentando(false);
        /* O vídeo da novidade espera a apresentação sair da frente (config/novidades.ts). */
        liberarHome();
        /* Se a marca falhar, a apresentação volta na próxima visita — melhor que
           travar a Home por causa dela. */
        NotificacaoService.marcarApresentacaoVista().catch(() => {});
    };

    /* Conta nova: o próximo dos Primeiros passos, que a sidebar já mediu. */
    const { visiveis, sinais } = usePrimeirosPassos();
    const proximoPasso = useMemo(
        () => visiveis.find(p => sinais[p.id] && !sinais[p.id]?.feito) ?? null,
        [visiveis, sinais],
    );

    const sugestoes: string[] = useMemo(() => {
        if (estado === 'erro') return SUGESTOES_DE_SEMPRE;
        if (estado !== 'pronto' || !abertura) return [];
        if (abertura.contaNova) {
            const pergunta = proximoPasso ? PERGUNTA_DO_PASSO[proximoPasso.id] : undefined;
            return [pergunta ?? 'Como começo a usar o Brik?'];
        }
        return abertura.sugestoes;
    }, [estado, abertura, proximoPasso]);

    /* ─── A conversa de hoje ─────────────────────────────────── */
    const {
        conversa,
        setConversa,
        mensagens,
        setMensagens,
        perguntando,
        parcial,
        consulta,
        degradadas,
        perguntar: perguntarNaConversa,
        avaliar,
        trocarAcao,
    } = useConversaAssistente();

    /* Quando chegou a última resposta: o Brik da faixa dá o pulinho. */
    const [respondeuEm, setRespondeuEm] = useState<number | null>(null);

    const perguntar = useCallback(async (texto: string, origem: OrigemPergunta = 'digitada') => {
        /* A rota diz à API que é a Home: "o que dá para fazer aqui?" ganha o artigo dela. */
        const dados = await perguntarNaConversa(texto, conversa?.conversaId, origem, { rota: '/home' });
        if (dados) setRespondeuEm(Date.now());
    }, [perguntarNaConversa, conversa]);

    /* ─── As conversas recentes, embaixo da tela inicial ─────────
       Pedido dele em 07/10: sair da Home e voltar abre sempre a tela inicial, não
       mais a última conversa, com as recentes embaixo para retomar uma ou começar
       outra. A lista acompanha a da sidebar (o mesmo aviso de conversas mudaram). */
    const [recentes, setRecentes] = useState<ConversaAssistente[] | null>(null);
    const reservaDoHistorico = useMemo(() => lerTamanhoDoHistorico(usuarioId), [usuarioId]);

    useEffect(() => {
        let vivo = true;
        const carregar = () => {
            AssistenteService.listarConversas(5)
                .then(lista => {
                    if (!vivo) return;
                    setRecentes(lista);
                    guardarTamanhoDoHistorico(usuarioId, lista.length);
                })
                /* Silencioso de propósito: a lista é acessório, e a Home continua
                   servindo para perguntar. */
                .catch(() => { if (vivo) setRecentes([]); });
        };
        carregar();
        window.addEventListener(CONVERSAS_ASSISTENTE_EVENT, carregar);
        return () => { vivo = false; window.removeEventListener(CONVERSAS_ASSISTENTE_EVENT, carregar); };
    }, [usuarioId]);

    /* ─── As novidades do produto, na tela inicial ─────────────────
       As linhas que reabrem as novidades em cartaz (08/10/2026). Enquanto se confere
       quais estão com a mídia no ar, o bloco guarda o lugar invisível: no caso normal,
       estão, e o conjunto centralizado não pula. */
    const vigentes = useMemo(() => novidadesVigentes(), []);
    const [novidadesNoAr, setNovidadesNoAr] = useState<Novidade[] | null>(vigentes.length ? null : []);
    useEffect(() => {
        if (!vigentes.length) return;
        let vivo = true;
        Promise.all(vigentes.map(n => midiaNoAr(n).then(ok => (ok ? n : null))))
            .then(r => { if (vivo) setNovidadesNoAr(r.filter((n): n is Novidade => n !== null)); });
        return () => { vivo = false; };
    }, [vigentes]);

    /* Uma conversa das recentes abre aqui mesmo, com a faixa do Brik no topo. */
    const abrirConversa = useCallback((id: number) => {
        AssistenteService.buscarConversa(id)
            .then(dados => {
                setConversa(dados.conversa);
                setMensagens(dados.mensagens);
                setRespondeuEm(null);
            })
            .catch(() => aviso.erro('Não foi possível abrir a conversa.'));
    }, [setConversa, setMensagens]);

    /* De volta à tela inicial: o botão da faixa, e o "Home" da sidebar clicado
       com a Home já aberta (mesma rota, visita nova). */
    const novaConversa = useCallback(() => {
        setConversa(null);
        setMensagens([]);
        setRespondeuEm(null);
    }, [setConversa, setMensagens]);

    const location = useLocation();
    const visita = useRef(location.key);
    useEffect(() => {
        if (location.key === visita.current) return;
        visita.current = location.key;
        if (!perguntando) novaConversa();
    }, [location.key, perguntando, novaConversa]);

    const conversando = mensagens.length > 0 || perguntando;

    /* Rola para o fim a cada mensagem — inclusive enquanto o "digitando" aparece. */
    const refFim = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (conversando) refFim.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [conversando, mensagens.length, perguntando, parcial]);

    /* `=== false` de propósito: quem logou antes de 13/09/2026 tem um `user` no
       localStorage sem este campo, e `undefined` não pode virar cobrança de
       confirmação para quem já confirmou. */
    const faixaEmail = usuario?.emailConfirmado === false
        ? <AvisoConfirmarEmail email={usuario?.email} />
        : null;

    const saudacaoDoDia = `${saudacao(new Date().getHours())}${nome ? `, ${nome}` : ''}!`;
    const placeholder = celular ? 'Responda ao Brik AI…' : 'Responda ao Brik AI ou peça outra coisa…';

    /* ─── A apresentação, uma vez por pessoa ─────────────────── */
    if (apresentando) {
        return (
            <div className="page-shell as-screen as-home">
                <div className="page-body as-centro">
                    <div className="as-coluna">
                        <BrikApresentacao nome={nome} onComecar={comecar} celular={celular} />
                    </div>
                </div>
            </div>
        );
    }

    /* ─── Com conversa: a faixa do Brik no topo e a conversa rolando por baixo ───
       Direção B, escolhida em 07/10/2026 (docs/designsystem/propostas/
       home-brik-conversa.html). O balão de abertura se recolhe na faixa: ele não
       é parte da conversa, e no topo dela ficava para sempre, mesmo com o assunto
       já em outra coisa, e subia com a rolagem levando o Brik junto. As respostas
       vão sem o Brik pequeno ao pé, como na tela cheia: quem pensa e pula é o da
       faixa. */
    if (conversando) {
        return (
            <div className="page-shell as-screen as-home as-conversa">
                <div className="page-body as-fill">
                    <div className="as-coluna hc-topo">
                        {faixaEmail}
                        <BrikFaixa
                            saudacao={saudacaoDoDia}
                            estado={estado}
                            abertura={abertura}
                            proximoPasso={proximoPasso?.titulo ?? null}
                            pensando={perguntando && !parcial}
                            falando={perguntando && !!parcial}
                            respondeuEm={respondeuEm}
                            onNovaConversa={novaConversa}
                            novaBloqueada={perguntando}
                            celular={celular}
                        />
                    </div>

                    {/* A área que rola ocupa a largura toda (a barra fica no canto da
                        tela); a coluna de leitura vai centralizada dentro dela. */}
                    <div className="as-rolagem">
                        <div className="as-coluna">
                            <div className="as-conv">
                                {mensagens.map((m, i) => (
                                    <AssistenteMensagem
                                        key={m.mensagemId}
                                        mensagem={m}
                                        degradada={degradadas.has(m.mensagemId)}
                                        onAvaliar={avaliar}
                                        onAcaoMudou={trocarAcao}
                                        /* A última resposta do modelo: é só nela que o Brik AI pergunta se ajudou. */
                                        ultima={!perguntando && m.papel === 'model' && !mensagens.slice(i + 1).some(o => o.papel === 'model')}
                                    />
                                ))}

                                {/* A resposta aparecendo enquanto é escrita; a gravada a substitui no fim. */}
                                {perguntando && parcial && (
                                    <AssistenteMensagem
                                        mensagem={{ mensagemId: -1, papel: 'model', conteudo: parcial, fontes: [], criadaEm: new Date().toISOString() }}
                                    />
                                )}

                                {perguntando && !parcial && (
                                    <div className="as-msg is-model">
                                        <AssistentePensando consulta={consulta} />
                                    </div>
                                )}
                                <div ref={refFim} />
                            </div>
                        </div>
                    </div>

                    <div className="as-coluna as-doca">
                        <AssistenteComposer onEnviar={texto => perguntar(texto)} enviando={perguntando} placeholder={placeholder} autoFocus />
                    </div>
                </div>
            </div>
        );
    }

    /* ─── Sem conversa: balão, campo e sugestões, centralizados na altura ─── */
    return (
        /* Sem PageHeader de propósito: a saudação é o título (o h1 do balão), e
           com o header a coluna ficava presa no topo com meia tela vazia embaixo —
           numa tela de pouco conteúdo, o conjunto se centraliza. */
        <div className="page-shell as-screen as-home">
            <div className="page-body as-centro as-com-selo">
                <SeloMeta />
                <div className="as-coluna">
                    {faixaEmail}
                    <BrikFala
                        saudacao={saudacaoDoDia}
                        estado={estado}
                        abertura={abertura}
                        proximoPasso={proximoPasso?.titulo ?? null}
                        celular={celular}
                    />

                    {/* A novidade fala pela boca do Brik, acima do campo: o
                        composer continua sendo só escrever e enviar. */}
                    {estado !== 'carregando' && (
                        <BrikNovidadeGanhas onTestar={pergunta => perguntar(pergunta, 'sugestao')} />
                    )}

                    <AssistenteComposer onEnviar={texto => perguntar(texto)} placeholder={placeholder} autoFocus />

                    {estado === 'carregando' && (
                        <div className="as-chips hc-reserva" aria-hidden="true">
                            {RESERVA_DAS_SUGESTOES.map(largura => (
                                <span key={largura} className="as-chip" style={{ width: largura }} />
                            ))}
                        </div>
                    )}

                    {sugestoes.length > 0 && (
                        <div className="as-chips">
                            {sugestoes.map(texto => (
                                <button
                                    key={texto}
                                    type="button"
                                    className="as-chip"
                                    onClick={() => perguntar(texto, 'sugestao')}
                                >
                                    {iconeDaSugestao(texto)}
                                    <span>{texto}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {(novidadesNoAr === null ? vigentes.length > 0 : novidadesNoAr.length > 0) && (
                        <div
                            className={`as-bloco${novidadesNoAr === null ? ' hc-reserva' : ''}`}
                            aria-hidden={novidadesNoAr === null ? true : undefined}
                        >
                            <div className="as-bloco-h">
                                <span className="as-bloco-t">Novidades</span>
                            </div>
                            <div className="as-recentes">
                                {(novidadesNoAr ?? vigentes).map(n => (
                                    <button
                                        key={n.chave}
                                        type="button"
                                        className="as-rec"
                                        onClick={() => abrirNovidade(n.chave)}
                                    >
                                        <LuPlay size={16} />
                                        <span className="as-rec-t">{n.titulo}</span>
                                        <span className="as-rec-d">{n.duracao ?? 'Vídeo'}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* As recentes: carregando, o bloco guarda o espaço que ocupou da
                        última vez, invisível; sem nenhuma conversa, não aparece. */}
                    {(recentes === null ? reservaDoHistorico > 0 : recentes.length > 0) && (
                        <div
                            className={`as-bloco${recentes === null ? ' hc-reserva' : ''}`}
                            aria-hidden={recentes === null ? true : undefined}
                        >
                            <div className="as-bloco-h">
                                <span className="as-bloco-t">Conversas recentes</span>
                                <button type="button" className="as-bloco-a" onClick={() => navigate('/assistente')}>
                                    Ver todas <LuChevronRight size={14} />
                                </button>
                            </div>

                            <div className="as-recentes">
                                {recentes === null
                                    ? Array.from({ length: reservaDoHistorico }, (_, i) => <span key={i} className="as-rec" />)
                                    : recentes.map(c => (
                                        <button
                                            key={c.conversaId}
                                            type="button"
                                            className="as-rec"
                                            onClick={() => abrirConversa(c.conversaId)}
                                        >
                                            <LuMessageSquare size={16} />
                                            <span className="as-rec-t">{c.titulo}</span>
                                            <span className="as-rec-d">{quando(c.atualizadaEm)}</span>
                                        </button>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HomePage;
